import { TagGroup, TagScope } from '@vivport/backend/types';
import { Save } from 'lucide-react';
import { inputScopeToOwnerScope } from '@/lib/owner-scope';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

export interface TagFormValues {
  name: string;
  description: string;
  scope: TagScope;
  tagGroupId: number | null;
  tagOrder?: number;
}

interface Work4MeTagFormProps {
  initialValues: TagFormValues;
  tagGroups: TagGroup[];
  isSubmitting: boolean;
  onSubmit: (values: TagFormValues) => Promise<void>;
  submitLabel: string;
  submittingLabel: string;
  showScopeSelector?: boolean;
}

export function Work4MeTagForm({
  initialValues,
  tagGroups,
  isSubmitting,
  onSubmit,
  submitLabel,
  submittingLabel,
  showScopeSelector = true,
}: Work4MeTagFormProps) {
  const [tagName, setTagName] = useState(initialValues.name);
  const [description, setDescription] = useState(initialValues.description);
  const [scope, setScope] = useState<TagScope>(initialValues.scope);
  const [selectedGroup, setSelectedGroup] = useState<string>(initialValues.tagGroupId ? String(initialValues.tagGroupId) : 'none');
  const [tagOrder, setTagOrder] = useState<string>(initialValues.tagOrder === undefined ? '' : String(initialValues.tagOrder));

  useEffect(() => {
    setTagName(initialValues.name);
    setDescription(initialValues.description);
    setScope(initialValues.scope);
    setSelectedGroup(initialValues.tagGroupId ? String(initialValues.tagGroupId) : 'none');
    setTagOrder(initialValues.tagOrder === undefined ? '' : String(initialValues.tagOrder));
  }, [initialValues]);

  const parsedTagOrder = useMemo(() => {
    if (tagOrder.trim() === '') return undefined;
    const parsed = Number(tagOrder);
    return Number.isInteger(parsed) ? parsed : undefined;
  }, [tagOrder]);

  const availableGroups = useMemo(() => {
    return tagGroups.filter((group) => group.ownerScope === inputScopeToOwnerScope(scope));
  }, [scope, tagGroups]);

  const selectedGroupData = selectedGroup === 'none'
    ? undefined
    : availableGroups.find((group) => group.id === Number(selectedGroup));

  const isFormValid = tagName.trim().length > 0;

  const handleSubmit = async () => {
    if (!isFormValid || isSubmitting) {
      return;
    }

    await onSubmit({
      name: tagName.trim(),
      description: description.trim(),
      scope,
      tagGroupId: selectedGroup === 'none' ? null : Number(selectedGroup),
      tagOrder: parsedTagOrder,
    });
  };

  return (
    <div className="space-y-5">
      <div>
        <Label htmlFor="tag-name">Tag Name *</Label>
        <Input
          id="tag-name"
          placeholder="e.g., In Review"
          value={tagName}
          onChange={(event) => setTagName(event.target.value)}
          className="mt-1"
        />
      </div>

      <div>
        <Label htmlFor="tag-description">Description</Label>
        <Textarea
          id="tag-description"
          placeholder="Optional description..."
          rows={3}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          className="mt-1 resize-none"
        />
      </div>

      {showScopeSelector ? (
        <div>
          <Label htmlFor="tag-scope">Scope</Label>
          <Select
            value={scope}
            onValueChange={(value) => {
              const nextScope = value as TagScope;
              setScope(nextScope);
              setSelectedGroup('none');
            }}
          >
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="PRIVATE">PRIVATE</SelectItem>
              <SelectItem value="HUB">HUB</SelectItem>
            </SelectContent>
          </Select>
        </div>
      ) : null}

      <div>
        <Label htmlFor="tag-group">Tag Group</Label>
        <Select value={selectedGroup} onValueChange={setSelectedGroup}>
          <SelectTrigger className="mt-1">
            <SelectValue placeholder="Select a tag group (optional)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Ungrouped</SelectItem>
            {availableGroups.map((group) => (
              <SelectItem key={group.id} value={group.id.toString()}>
                {group.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedGroupData ? (
          <div className="mt-2 p-3 bg-muted/50 rounded-md">
            <p className="text-xs text-muted-foreground">{selectedGroupData.description ?? 'No description'}</p>
          </div>
        ) : null}
      </div>

      <div>
        <Label htmlFor="tag-order">Order</Label>
        <Input
          id="tag-order"
          value={tagOrder}
          onChange={(event) => setTagOrder(event.target.value)}
          placeholder="Optional integer"
          className="mt-1"
        />
      </div>

      <Button
        onClick={handleSubmit}
        className="w-full gap-2"
        disabled={!isFormValid || isSubmitting}
      >
        {isSubmitting ? (
          submittingLabel
        ) : (
          <>
            <Save className="h-4 w-4" />
            {submitLabel}
          </>
        )}
      </Button>
    </div>
  );
}

